import React, { useEffect, useState, useCallback } from 'react';
import { accountAPI } from '../lib/api';
import { apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import {
  Plus,
  Edit2,
  Trash2,
  Loader2,
  Landmark,
  CreditCard,
  Banknote,
  CheckCircle2,
} from 'lucide-react';

const TYPE_META = {
  bank: { label: 'Bank', icon: Landmark },
  credit_card: { label: 'Credit Card', icon: CreditCard },
  cash: { label: 'Cash', icon: Banknote },
  cash_credit: { label: 'Cash Credit / OD', icon: Landmark },
  loan: { label: 'Loan', icon: Landmark },
};

// Types that are real bank accounts: they carry bank_details and can print on invoices.
const BANK_TYPES = ['bank', 'cash_credit'];

const EMPTY_FORM = () => ({
  name: '',
  type: 'bank',
  due_day: '',
  last4: '',
  show_on_invoice: false,
  status: 'active',
  bank_details: {
    account_name: '',
    account_number: '',
    ifsc: '',
    account_type: 'Current',
    bank_name: '',
    branch_name: '',
    upi_id: '',
  },
});

export default function Accounts() {
  const { can } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());

  const fetchData = useCallback(async () => {
    try {
      const res = await accountAPI.getAll();
      setAccounts(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Failed to load accounts'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const openNew = () => {
    setEditingId(null);
    setForm(EMPTY_FORM());
    setOpen(true);
  };

  const openEdit = (acc) => {
    setEditingId(acc.id);
    setForm({
      name: acc.name || '',
      type: acc.type || 'bank',
      due_day: acc.due_day || '',
      last4: acc.last4 || '',
      show_on_invoice: !!acc.show_on_invoice,
      status: acc.status || 'active',
      bank_details: {
        account_name: acc.bank_details?.account_name || '',
        account_number: acc.bank_details?.account_number || '',
        ifsc: acc.bank_details?.ifsc || '',
        account_type: acc.bank_details?.account_type || 'Current',
        bank_name: acc.bank_details?.bank_name || '',
        branch_name: acc.bank_details?.branch_name || '',
        upi_id: acc.bank_details?.upi_id || '',
      },
    });
    setOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Name is required');
    setSaving(true);
    try {
      // PUT is partial: balances (set by bank reconciliation) are never sent, so
      // they survive an edit. Non-bank types keep whatever bank_details are stored.
      // The invoice footer only prints type 'bank' accounts (server.py).
      const { bank_details, ...rest } = form;
      const payload = { ...rest, show_on_invoice: form.type === 'bank' && form.show_on_invoice };
      if (BANK_TYPES.includes(form.type)) payload.bank_details = bank_details;
      if (editingId) {
        await accountAPI.update(editingId, payload);
        toast.success('Account updated');
      } else {
        await accountAPI.create(payload);
        toast.success('Account added');
      }
      setOpen(false);
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Save failed'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this account? Linked expenses/income will keep the old account_id reference until you reassign them.')) return;
    try {
      await accountAPI.delete(id);
      toast.success('Account deleted');
      fetchData();
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  // Closed accounts stay listed (with a badge) but sort last.
  const list = [...accounts].sort((a, b) => (a.status === 'closed') - (b.status === 'closed'));

  return (
    <div className="space-y-8 animate-fadeIn">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Accounts</h1>
          <p className="text-gray-500 mt-1">
            Bank, cash and card accounts for DESENO. Mark which bank appears on invoice footers.
          </p>
        </div>
        {can('write') && (
          <Button onClick={openNew} className="bg-black hover:bg-black/90">
            <Plus className="w-4 h-4 mr-1" /> Add account
          </Button>
        )}
      </div>

      {list.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center justify-between">
              <span>DESENO</span>
              <span className="text-sm text-gray-400 font-normal">{list.length} accounts</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm min-w-[640px]">
                <thead className="border-b">
                  <tr className="text-left text-gray-500">
                    <th className="py-2 px-4 font-medium">Name</th>
                    <th className="py-2 px-4 font-medium">Type</th>
                    <th className="py-2 px-4 font-medium">Bank / Last 4</th>
                    <th className="py-2 px-4 font-medium text-center">Due day</th>
                    <th className="py-2 px-4 font-medium text-center">On invoice</th>
                    <th className="py-2 px-4 w-[120px]"></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((a) => {
                    const Icon = TYPE_META[a.type]?.icon || Landmark;
                    const closed = a.status === 'closed';
                    return (
                      <tr key={a.id} className={`border-b last:border-b-0 hover:bg-gray-50 ${closed ? 'text-gray-400' : ''}`}>
                        <td className="py-3 px-4 font-medium">
                          <div className="flex items-center gap-2">
                            <Icon size={16} className="text-gray-500" />
                            {a.name}
                            {closed && <span className="badge badge-gray">Closed</span>}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          {TYPE_META[a.type]?.label || a.type}
                        </td>
                        <td className="py-3 px-4 text-gray-600 text-xs">
                          {a.bank_details?.bank_name && (
                            <>
                              {a.bank_details.bank_name}
                              {a.bank_details.account_number && ` · …${a.bank_details.account_number.slice(-4)}`}
                            </>
                          )}
                          {a.last4 && <>…{a.last4}</>}
                        </td>
                        <td className="py-3 px-4 text-center font-mono text-xs">{a.due_day || '—'}</td>
                        <td className="py-3 px-4 text-center">
                          {a.show_on_invoice && <CheckCircle2 size={16} className="inline text-black" />}
                        </td>
                        <td className="py-3 px-4 text-right whitespace-nowrap">
                          {can('write') && (
                            <Button variant="ghost" size="sm" onClick={() => openEdit(a)} title="Edit">
                              <Edit2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {can('delete') && (
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(a.id)} title="Delete">
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {accounts.length === 0 && (
        <Card>
          <CardContent className="py-16 text-center text-gray-400">
            No accounts yet.{can('write') && <> Click <b>Add account</b> to start.</>}
          </CardContent>
        </Card>
      )}

      {/* Add / Edit modal */}
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editingId ? 'Edit account' : 'Add account'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Display name *</Label>
                <Input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g. HDFC Bank (DESENO)"
                  required
                />
              </div>
              <div className="space-y-1.5">
                <Label>Type *</Label>
                <Select value={form.type} onValueChange={(v) => setForm({ ...form, type: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="bank">Bank</SelectItem>
                    <SelectItem value="credit_card">Credit Card</SelectItem>
                    <SelectItem value="cash">Cash</SelectItem>
                    <SelectItem value="cash_credit">Cash Credit / OD</SelectItem>
                    <SelectItem value="loan">Loan</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Due day {form.type === 'credit_card' ? '*' : ''}</Label>
                <Input
                  value={form.due_day}
                  onChange={(e) => setForm({ ...form, due_day: e.target.value })}
                  placeholder="e.g. 08"
                />
              </div>
            </div>

            {form.type === 'credit_card' && (
              <div className="space-y-1.5">
                <Label>Last 4 digits</Label>
                <Input
                  value={form.last4}
                  onChange={(e) => setForm({ ...form, last4: e.target.value })}
                  placeholder="1234"
                  maxLength={4}
                />
              </div>
            )}

            {BANK_TYPES.includes(form.type) && (
              <div className="space-y-3 bg-gray-50 rounded-md p-3">
                <h3 className="text-sm font-medium">Bank details{form.type === 'bank' && ' (shown on invoice footer)'}</h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <div className="space-y-1.5 col-span-1 sm:col-span-2">
                    <Label className="text-xs">Account holder name</Label>
                    <Input
                      value={form.bank_details.account_name}
                      onChange={(e) => setForm({
                        ...form,
                        bank_details: { ...form.bank_details, account_name: e.target.value },
                      })}
                      placeholder="DESENO MEDIA AGENCY"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Bank name</Label>
                    <Input
                      value={form.bank_details.bank_name}
                      onChange={(e) => setForm({
                        ...form,
                        bank_details: { ...form.bank_details, bank_name: e.target.value },
                      })}
                      placeholder="HDFC BANK"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Account type</Label>
                    <Select
                      value={form.bank_details.account_type}
                      onValueChange={(v) => setForm({
                        ...form,
                        bank_details: { ...form.bank_details, account_type: v },
                      })}
                    >
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Current">Current</SelectItem>
                        <SelectItem value="Savings">Savings</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Account number</Label>
                    <Input
                      value={form.bank_details.account_number}
                      onChange={(e) => setForm({
                        ...form,
                        bank_details: { ...form.bank_details, account_number: e.target.value },
                      })}
                      placeholder="50200060661773"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">IFSC code</Label>
                    <Input
                      value={form.bank_details.ifsc}
                      onChange={(e) => setForm({
                        ...form,
                        bank_details: { ...form.bank_details, ifsc: e.target.value.toUpperCase() },
                      })}
                      placeholder="HDFC0002541"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">Branch name</Label>
                    <Input
                      data-testid="account-branch-name"
                      value={form.bank_details.branch_name}
                      onChange={(e) => setForm({
                        ...form,
                        bank_details: { ...form.bank_details, branch_name: e.target.value },
                      })}
                      placeholder="Nashik Main Branch"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label className="text-xs">UPI ID</Label>
                    <Input
                      data-testid="account-upi-id"
                      value={form.bank_details.upi_id}
                      onChange={(e) => setForm({
                        ...form,
                        bank_details: { ...form.bank_details, upi_id: e.target.value.trim() },
                      })}
                      placeholder="deseno@hdfcbank"
                    />
                  </div>
                </div>
              </div>
            )}

            {form.type === 'bank' && (
              <div className="flex items-center justify-between bg-gray-50 rounded-md px-3 py-2">
                <div>
                  <Label htmlFor="acc-show-on-invoice" className="text-sm font-medium">Show on invoices</Label>
                  <p className="text-xs text-gray-500">Include these bank details in the footer of every DESENO invoice (needs an account number)</p>
                </div>
                <Switch
                  id="acc-show-on-invoice"
                  checked={form.show_on_invoice}
                  onCheckedChange={(v) => setForm({ ...form, show_on_invoice: v })}
                />
              </div>
            )}

            <div className="flex justify-end gap-2 pt-2">
              <Button type="button" variant="outline" onClick={() => setOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : (editingId ? 'Save changes' : 'Add account')}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
